import { createFileRoute, Link } from "@tanstack/react-router";
import { Wrench, ShieldCheck, PenLine, Lock, FileText, Newspaper, Image, ClipboardList, ShoppingBag, ArrowRightLeft } from "lucide-react";
import { serviceSchemaScripts } from "@/lib/service-schema";
import { freshnessMeta, webPageSchemaScript } from "@/lib/page-freshness";
import { FaqSection, faqSchemaScript, type FaqItem } from "@/components/landing/FaqSection";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { ParticleBackground } from "@/components/landing/ParticleBackground";
import { PageIntro } from "@/components/landing/PageIntro";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { ClosingCTA } from "@/components/landing/ClosingCTA";
import { RelatedServices } from "@/components/landing/RelatedServices";
import heroVideoAsset from "@/assets/squarespace-hero.webm.asset.json";
import heroPosterAsset from "@/assets/squarespace-hero-poster.jpg.asset.json";
import circleBadge from "@/assets/circle-badge.webp.asset.json";

const PATH = "/squarespace-migrations";
const URL = `https://www.squaremigration.com${PATH}`;
const PUBLISHED = "2026-10-23T00:00:00+00:00";
const MODIFIED = "2026-10-23T00:00:00+00:00";
const TITLE = "Squarespace Migrations: WordPress, Wix & Webflow to Squarespace | Square Migration";
const DESCRIPTION =
  "Move your website to Squarespace from WordPress, Wix, Webflow, GoDaddy or a custom CMS, with content, redirects, SEO, domain and email handled.";

const FAQS: FaqItem[] = [
  {
    question: "What does a Squarespace migration cost?",
    answer: "It depends on page count, blog and product volume, custom functionality and how much redesign you want along the way. Send your URL and we will scope it after a quick inventory.",
  },
  {
    question: "How long does a migration take?",
    answer: "Timing depends on the size of the site and how quickly content decisions get made. We set a launch date together after the audit and plan around it.",
  },
  {
    question: "Will we lose our Google rankings?",
    answer: "Not if the move is planned. We map every old URL to its new page with 301 redirects, carry over titles and descriptions, resubmit the sitemap and monitor Search Console after launch.",
  },
  {
    question: "Can we keep our domain where it is registered now?",
    answer: "Yes. Your domain can stay at its current registrar. We update the DNS records to point to Squarespace, or transfer it if you prefer one place to manage everything.",
  },
  {
    question: "What happens to our email?",
    answer: "Email is separate from your website. We preserve your existing mail records during the DNS change so email keeps working without interruption.",
  },
];

const REASONS = [
  { icon: Wrench, title: "Lower upkeep", description: "No servers, themes or builds to babysit. The platform handles the plumbing." },
  { icon: ShieldCheck, title: "No plugin patching", description: "Skip the constant plugin updates and security fixes that WordPress sites demand." },
  { icon: PenLine, title: "Edit without a developer", description: "Your team updates copy, images and pages on its own." },
  { icon: Lock, title: "Hosting and SSL included", description: "Fast global hosting and security certificates come built in." },
];

const MIGRATED = [
  { icon: FileText, label: "Pages and navigation" },
  { icon: Newspaper, label: "Blog posts and categories" },
  { icon: Image, label: "Images and media" },
  { icon: ClipboardList, label: "Forms and lead capture" },
  { icon: ShoppingBag, label: "Products and store" },
  { icon: ArrowRightLeft, label: "Old URLs and redirects" },
];

const PLATFORMS = [
  { name: "WordPress", note: "The most common move. Posts, pages and media carried over, plugins replaced with native features." },
  { name: "Wix", note: "Content rebuilt in a cleaner structure with proper redirects from Wix URLs." },
  { name: "Webflow", note: "Layouts recreated so your team can edit without designer-level tools." },
  { name: "GoDaddy", note: "Website builder sites upgraded to a more flexible, modern platform." },
  { name: "Drupal", note: "Complex content types simplified into pages your team can actually manage." },
  { name: "Custom builds", note: "Hand-coded or legacy CMS sites moved off developer dependency." },
];

const SEO_STEPS = [
  "A full 301 redirect map from every old URL to its new home",
  "Matching page titles, descriptions and headings",
  "Sitemap resubmitted in Google Search Console",
  "Post-launch checks for broken links and crawl errors",
];

export const Route = createFileRoute("/squarespace-migrations")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://www.squaremigration.com/og-image.jpg" },
      { name: "twitter:image", content: "https://www.squaremigration.com/og-image.jpg" },
      ...freshnessMeta({ published: PUBLISHED, modified: MODIFIED }),
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      ...serviceSchemaScripts({
        path: PATH,
        name: "Squarespace Migrations",
        description: DESCRIPTION,
        serviceType: "Website migration to Squarespace",
      }),
      faqSchemaScript(FAQS),
      webPageSchemaScript({ path: PATH, name: TITLE, description: DESCRIPTION, published: PUBLISHED, modified: MODIFIED }),
    ],
  }),
  component: SquarespaceMigrationsPage,
});

function Eyebrow({ children }: { children: React.ReactNode }) {
  return <p className="text-xs font-semibold tracking-[0.22em] uppercase text-primary mb-4">{children}</p>;
}

function SquarespaceMigrationsPage() {
  return (
    <main className="relative">
      <ParticleBackground />
      <Header />
      <PageIntro
        eyebrow="Squarespace Migrations"
        title="Move to Squarespace without losing your traffic."
        description="We move websites from WordPress, Wix, Webflow, GoDaddy and custom platforms to Squarespace, with content, redirects, SEO, domain and email handled carefully."
        bullets={["Content migration", "Redirects and SEO kept", "Domain and email setup", "Team training"]}
        backgroundVideo={{
          src: heroVideoAsset.url,
          poster: heroPosterAsset.url,
          ariaLabel: "Squarespace site on a phone, floating in a dark room",
        }}
      />

      <section className="border-t border-border/50 bg-[#080E1E]/95 px-5 py-12 sm:px-6 sm:py-16 md:px-8 md:py-20 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <Eyebrow>Why Teams Switch</Eyebrow>
          <h2 className="text-3xl sm:text-4xl font-semibold leading-tight max-w-3xl">
            Less time maintaining the site, <span className="text-primary">more time using it.</span>
          </h2>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 sm:gap-6">
            {REASONS.map(({ icon: Icon, title, description }) => (
              <div key={title} className="rounded-xl border border-border/50 bg-card/30 p-6">
                <Icon className="w-6 h-6 text-primary mb-4" />
                <h3 className="text-lg font-semibold mb-2">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border/50 px-5 py-12 sm:px-6 sm:py-16 md:px-8 md:py-20 lg:px-12">
        <div className="max-w-6xl mx-auto grid gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <Eyebrow>What We Migrate</Eyebrow>
            <h2 className="text-3xl font-semibold leading-tight">Everything that matters comes with you.</h2>
            <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {MIGRATED.map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-center gap-3 rounded-lg border border-border/50 bg-card/30 px-4 py-3 text-sm">
                  <Icon className="w-5 h-5 text-primary shrink-0" />
                  {label}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <Eyebrow>Platforms We Move From</Eyebrow>
            <h2 className="text-3xl font-semibold leading-tight">Wherever your site lives today.</h2>
            <dl className="mt-8 space-y-4">
              {PLATFORMS.map((p) => (
                <div key={p.name} className="border-l-2 border-primary pl-4">
                  <dt className="font-semibold">{p.name}</dt>
                  <dd className="text-sm text-muted-foreground leading-relaxed">{p.note}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      <ProcessSection
        eyebrow="Migration Process"
        title="A planned move, not a copy and paste."
        lead="Each stage protects what you have already built, from content to search rankings."
        steps={[
          { number: "01", title: "Inventory & Audit", description: "Catalog every page, post, form and file, and flag what is worth keeping." },
          { number: "02", title: "Map URLs & Structure", description: "Plan the new navigation and match every old URL to its new destination." },
          { number: "03", title: "Rebuild & Move Content", description: "Design in Squarespace and carry content over, cleaned up along the way." },
          { number: "04", title: "Launch & Verify", description: "Redirects, analytics and Search Console set up, then checked after go-live." },
        ]}
      />

      <section className="border-t border-border/50 bg-[#080E1E]/95 px-5 py-12 sm:px-6 sm:py-16 md:px-8 md:py-20 lg:px-12">
        <div className="max-w-6xl mx-auto grid gap-10 lg:grid-cols-2 lg:gap-16 lg:items-start">
          <div>
            <Eyebrow>SEO Protection</Eyebrow>
            <h2 className="text-3xl sm:text-4xl font-semibold leading-tight">
              Keep the rankings <span className="text-primary">you worked for.</span>
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Most traffic losses after a website move come from broken links and missing redirects. We plan for search before anything changes.
            </p>
          </div>
          <ul className="space-y-3">
            {SEO_STEPS.map((s) => (
              <li key={s} className="flex gap-3 rounded-lg border border-border/50 bg-card/30 p-4 text-sm leading-relaxed">
                <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                {s}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="border-t border-border/50 px-5 py-12 sm:px-6 sm:py-16 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto flex flex-col items-start gap-6 sm:flex-row sm:items-center">
          <img src={circleBadge.url} alt="Squarespace Circle Member badge" className="h-14 w-auto" loading="lazy" />
          <p className="text-muted-foreground leading-relaxed">
            <span className="text-foreground font-semibold">Where we started.</span> Square Migration was founded in 2021 doing exactly this work: moving businesses onto Squarespace. We are an official Squarespace Circle Member.{" "}
            <Link to="/about" className="text-primary hover:underline">Read our story</Link>
            {" "}or see our{" "}
            <Link to="/squarespace-websites" className="text-primary hover:underline">Squarespace website services</Link>. Worried about WordPress security? Read our{" "}
            <Link to="/php-vulnerability-squarespace" className="text-primary hover:underline">php2shell PHP vulnerability explainer</Link>.
          </p>
        </div>
      </section>

      <ClosingCTA
        eyebrow="Ready to Move"
        headline={
          <>
            Stuck on a platform <span className="text-primary">that fights you?</span>
          </>
        }
        subhead="Let's plan the move."
        body="Send your current URL and we will review what you have, what should come over and how to protect your traffic."
        ctaLabel="Review My Current Site"
      />
      <FaqSection items={FAQS} />
      <RelatedServices current={PATH} />
      <Footer />
    </main>
  );
}
